import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { THEME_KEY } from "../../constant/constant";

export const THEMES = [
  "default",
  "custom-1",
  "custom-2",
  "custom-3",
  "custom-4",
];

export interface ThemeWrapperState {
  theme: typeof THEMES[number];
  COLORS: ThemeColors;
  footerVisible: boolean;
  snowFlakeButtonVisible: boolean;
}

export interface ThemeColors {
  primary: string;
  secondary: string;
  tertiary: string;
  info: string;
  success: string;
  warning: string;
  lightGrey: string;
  grey: string;
  darkGrey: string;
  white: string;
  black: string;
}

export const THEME_COLORS: Record<typeof THEMES[number], ThemeColors> = {
  default: {
    primary: "#bb2019",
    secondary: "#FF9999",
    tertiary: "#FFF4F4",
    info: "#306da6",
    success: "#57b056",
    warning: "#bb2019",
    lightGrey: "#dddddd",
    grey: "#bbbbbb",
    darkGrey: "#808080",
    white: "#ffffff",
    black: "#000000",
  },
  "custom-1": {
    primary: "#bb2019",
    secondary: "#c2b280",
    tertiary: "#f2e8d9",
    info: "#99b899",
    success: "#9fa860",
    warning: "#d79920",
    lightGrey: "#e5e5e5",
    grey: "#c0c0c0",
    darkGrey: "#999999",
    white: "#ffffff",
    black: "#333333",
  },
  "custom-2": {
    primary: "#3f51b5",
    secondary: "#566573",
    tertiary: "#e2e8f0",
    info: "#007bff",
    success: "#4caf50",
    warning: "#ffc107",
    lightGrey: "#e0e0e0",
    grey: "#bdbdbd",
    darkGrey: "#757575",
    white: "#ffffff",
    black: "#000000",
  },
  "custom-3": {
    primary: "#009688",
    secondary: "#b2d0c7",
    tertiary: "#e0e8ef",
    info: "#007bff",
    success: "#4caf50",
    warning: "#ffc107",
    lightGrey: "#e0e0e0",
    grey: "#bdbdbd",
    darkGrey: "#757575",
    white: "#ffffff",
    black: "#000000",
  },
  "custom-4": {
    primary: "#333333",
    secondary: "#c0c0c0",
    tertiary: "#e0e0e0",
    info: "#007bff",
    success: "#28a745",
    warning: "#dc3545",
    lightGrey: "#e5e5e5",
    grey: "#c0c0c0",
    darkGrey: "#757575",
    white: "#ffffff",
    black: "#000000",
  },
};

const initialState: ThemeWrapperState = {
  theme: "default",
  COLORS: THEME_COLORS["default"],
  footerVisible: true,
  snowFlakeButtonVisible: true,
};

export const themeWrapperSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<typeof THEMES[number]>) => {
      state.theme = action.payload;
      state.COLORS = THEME_COLORS[action.payload];
      window.localStorage.setItem(THEME_KEY, action.payload);
    },
    loadThemeWrapperFromStorage: (state) => {
      const newTheme = (state.theme =
        state.theme === null
          ? THEMES[0]
          : (window.localStorage.getItem(THEME_KEY) as typeof THEMES[number]));
      if (state.theme === null) {
        window.localStorage.setItem(THEME_KEY, newTheme);
      }
      state.COLORS = THEME_COLORS[newTheme];
    },
    showFooter: (state) => {
      state.footerVisible = true;
    },
    hideFooter: (state) => {
      state.footerVisible = false;
    },
    showSnowFlakeButton: (state) => {
      state.snowFlakeButtonVisible = true;
    },
    hideSnowFlakeButton: (state) => {
      state.snowFlakeButtonVisible = false;
    },
  },
});

export const {
  setTheme,
  loadThemeWrapperFromStorage,
  showFooter,
  hideFooter,
  showSnowFlakeButton,
  hideSnowFlakeButton,
} = themeWrapperSlice.actions;
